/**
 * CutCalc Project File (.cutcalc) Utilities
 *
 * File format is versioned JSON for backwards compatibility.
 * Current version: 1
 */

// Current file format version
export const CUTCALC_VERSION = 2;

/**
 * Create a .cutcalc project file from layout data
 * @param {Object} data - Project data to export
 * @param {Array} data.layouts - Optimized layout data
 * @param {Array} data.pieces - Original pieces/parts list
 * @param {Object} data.currentUnits - Unit settings { system, unit }
 * @param {number} data.sheetWidth - Sheet width in inches
 * @param {number} data.sheetLength - Sheet length in inches
 * @param {number} data.cutWidth - Blade kerf width in inches
 * @param {boolean} [data.trimFactoryEdges] - Whether to trim factory edges
 * @param {number} [data.trimAmount] - Amount to trim from each edge in inches
 * @param {Object} [data.stats] - Optimization statistics
 * @param {Object} [data.modelSnapshot] - Optional 3D model snapshot
 * @param {Array} [data.stockSheetTypes] - Stock sheet type definitions
 * @returns {Object} Project file object
 */
export const createProjectFile = ({
  layouts,
  pieces = [],
  currentUnits,
  sheetWidth,
  sheetLength,
  cutWidth,
  trimFactoryEdges = false,
  trimAmount = 0,
  stats = null,
  modelSnapshot = null,
  stockSheetTypes = [],
}) => {
  return {
    version: CUTCALC_VERSION,
    exportedAt: new Date().toISOString(),
    settings: {
      currentUnits,
      sheetWidth,
      sheetLength,
      cutWidth,
      trimFactoryEdges,
      trimAmount,
    },
    stockSheetTypes,
    pieces,
    layouts,
    stats,
    modelSnapshot,
  };
};

/**
 * Export project as .cutcalc file download
 * @param {Object} projectData - Data to export (see createProjectFile params)
 * @param {string} [filename] - Optional filename (without extension)
 */
export const exportCutcalcFile = (projectData, filename = null) => {
  const project = createProjectFile(projectData);
  const json = JSON.stringify(project, null, 2);
  const blob = new Blob([json], { type: 'application/json' });

  const defaultFilename = `cutcalc-project-${new Date().toISOString().split('T')[0]}`;
  const finalFilename = `${filename || defaultFilename}.cutcalc`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = finalFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return { success: true, filename: finalFilename };
};

/**
 * Parse and validate a .cutcalc file
 * @param {string} jsonString - Raw JSON string from file
 * @returns {Object} Parsed and migrated project data
 * @throws {Error} If file is invalid or cannot be migrated
 */
export const parseCutcalcFile = (jsonString) => {
  let data;

  try {
    data = JSON.parse(jsonString);
  } catch (e) {
    throw new Error('Invalid file format: not valid JSON');
  }

  // Check for version
  if (!data.version) {
    throw new Error('Invalid file format: missing version');
  }

  // Check for required fields
  if (!data.layouts || !Array.isArray(data.layouts)) {
    throw new Error('Invalid file format: missing layouts');
  }

  if (!data.settings) {
    throw new Error('Invalid file format: missing settings');
  }

  // Migrate if needed
  const migrated = migrateProject(data);

  return migrated;
};

/**
 * Read and parse a .cutcalc file from File object
 * @param {File} file - File object from input
 * @returns {Promise<Object>} Parsed project data
 */
export const readCutcalcFile = (file) => {
  return new Promise((resolve, reject) => {
    // Validate file extension
    if (!file.name.endsWith('.cutcalc') && !file.name.endsWith('.json')) {
      reject(new Error('Invalid file type. Please select a .cutcalc file.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const project = parseCutcalcFile(e.target.result);
        resolve(project);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Migrate project data from older versions to current version
 * @param {Object} project - Project data with version field
 * @returns {Object} Migrated project data at current version
 */
const migrateProject = (project) => {
  let current = { ...project };

  // Migration chain: each version migrates to the next
  // Add new migrations here as versions increase

  if (current.version === 1) {
    current = migrateV1toV2(current);
  }

  if (current.version === 2) {
    // Current version, no migration needed
    return current;
  }

  // If version is higher than current, warn but try to load
  if (current.version > CUTCALC_VERSION) {
    console.warn(`File version ${current.version} is newer than app version ${CUTCALC_VERSION}. Some features may not work correctly.`);
  }

  return current;
};

/**
 * Migrate v1 → v2: Rename .height → .length on pieces, stock sheet types, and settings
 */
const migrateV1toV2 = (project) => {
  const migrated = { ...project, version: 2 };

  // Migrate settings
  if (migrated.settings && 'sheetHeight' in migrated.settings) {
    migrated.settings = { ...migrated.settings, sheetLength: migrated.settings.sheetHeight };
    delete migrated.settings.sheetHeight;
  }

  // Migrate pieces
  if (migrated.pieces) {
    migrated.pieces = migrated.pieces.map(p => {
      if ('height' in p) {
        const { height, ...rest } = p;
        return { ...rest, length: height };
      }
      return p;
    });
  }

  // Migrate stock sheet types
  if (migrated.stockSheetTypes) {
    migrated.stockSheetTypes = migrated.stockSheetTypes.map(t => {
      if ('height' in t) {
        const { height, ...rest } = t;
        return { ...rest, length: height };
      }
      return t;
    });
  }

  // Migrate layouts
  if (migrated.layouts) {
    migrated.layouts = migrated.layouts.map(layout => {
      const l = { ...layout };
      if ('height' in l) {
        l.length = l.height;
        delete l.height;
      }
      if (l.pieces) {
        l.pieces = l.pieces.map(p => {
          if ('height' in p) {
            const { height, ...rest } = p;
            return { ...rest, length: height };
          }
          return p;
        });
      }
      return l;
    });
  }

  return migrated;
};

/**
 * Validate that a project file can be loaded into shop sheet view
 * @param {Object} project - Parsed project data
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateProject = (project) => {
  const errors = [];

  if (!project.layouts || project.layouts.length === 0) {
    errors.push('Project contains no layouts');
  }

  if (!project.settings?.currentUnits) {
    errors.push('Project is missing unit settings');
  }

  if (typeof project.settings?.sheetWidth !== 'number') {
    errors.push('Project is missing sheet width');
  }

  if (typeof project.settings?.sheetLength !== 'number') {
    errors.push('Project is missing sheet length');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};
